/**
 * RodeoLivePage — public live-event board for a rodeo.
 *
 * Surfaces the single event an admin/delegate has flagged as the rodeo's
 * "current live event" (events.is_current_live_event) plus its cumulative
 * leaderboard. Polls so spectators see standings update as the secretary
 * enters scores. When no event is flagged, the page shows a neutral
 * "nothing live right now" state.
 */

import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Radio, Trophy } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useRodeoCurrentLiveEvent,
  type LiveLeaderboardEntry,
} from '@/api/extensions/useRodeoScoringOps';
import { useRodeoDetail } from '@/api/extensions/useRodeoOps';
import { resolveEventScoring, timedScoreDecimals } from '@/constants/event-types';

const PLACE_ICON: Record<number, string> = { 1: '🏆', 2: '🥈', 3: '🥉' };

export default function RodeoLivePage() {
  const { rodeoId } = useParams<{ rodeoId: string }>();
  const navigate = useNavigate();

  const { data: rodeo } = useRodeoDetail(rodeoId ?? '');
  const { data, isLoading } = useRodeoCurrentLiveEvent(rodeoId ?? undefined, {
    refetchInterval: 10_000,
  });

  const rodeoName = (rodeo as any)?.name ?? 'Rodeo';
  const liveEvent = data?.event ?? null;
  const entries: LiveLeaderboardEntry[] = data?.entries ?? [];
  // Judged events rank by score (high wins); timed events by time (low wins).
  const isScored = entries.some((e) => e.final_score != null);
  // Barrel racing times show to the thousandth; other timed events to hundredths.
  const timeDecimals = timedScoreDecimals(resolveEventScoring(liveEvent as any).isBarrelRacing);

  const fmt = (e: LiveLeaderboardEntry) => {
    if (e.did_not_perform) return 'DNP';
    if (e.is_disqualified) return 'DQ';
    if (e.no_time) return 'NT';
    if (isScored) return e.final_score != null ? Number(e.final_score).toFixed(1) : '—';
    return e.final_time != null ? `${Number(e.final_time).toFixed(timeDecimals)}s` : '—';
  };

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-4">
      <button
        onClick={() => navigate(`/rodeos/${rodeoId}`)}
        className="text-blue-500 hover:text-blue-400 text-sm flex items-center gap-1"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to {rodeoName}
      </button>

      <div className="flex items-center gap-2">
        <Radio className="h-6 w-6 text-red-500" />
        <h1 className="text-2xl font-bold">{rodeoName} — Live</h1>
      </div>

      {isLoading ? (
        <Skeleton className="h-48 rounded-lg" />
      ) : !liveEvent ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-16">
            <Radio className="h-12 w-12 text-muted-foreground/30" />
            <p className="text-muted-foreground text-center">
              Nothing is being scored live right now. Check back when an event is in progress.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
              <span className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" />
                {liveEvent.event_name ?? 'Current Event'}
              </span>
              <span className="flex items-center gap-2">
                {liveEvent.scoring_state === 'live' ? (
                  <Badge className="bg-red-600 hover:bg-red-600 text-white">LIVE</Badge>
                ) : (
                  <Badge variant="secondary">{liveEvent.scoring_state.toUpperCase()}</Badge>
                )}
                <Badge variant="outline" className="font-normal text-xs">
                  {isScored ? 'High score wins' : 'Fast time wins'}
                </Badge>
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {entries.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No scores recorded yet.
              </p>
            ) : (
              <div className="space-y-1.5">
                {entries.map((e) => {
                  const icon = e.place ? PLACE_ICON[e.place] : undefined;
                  return (
                    <div
                      key={e.registration_id}
                      className={`flex items-center gap-3 rounded-md border px-3 py-2 ${
                        e.place != null && e.place <= 3
                          ? 'border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/20'
                          : ''
                      }`}
                    >
                      <div className="flex w-8 shrink-0 items-center gap-1">
                        {icon && <span>{icon}</span>}
                        <span className="font-bold tabular-nums">{e.place ?? '—'}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">
                          {[e.contestant_name, e.partner_name].filter(Boolean).join(' / ') ||
                            'Entry'}
                        </p>
                      </div>
                      <div className="shrink-0 text-right font-mono text-sm font-bold tabular-nums">
                        {fmt(e)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
